/**
 * Deciding what a recipe's `readyInMinutes` should be.
 *
 * `readyInMinutes` is what every filter, badge, shelf and slot scorer reads,
 * and it is stored alongside `preparationMinutes` and `cookingMinutes` that
 * nothing keeps it in step with. "Gigot d'agneau de 7 heures" sat at
 * `readyInMinutes: 10` with `cookingMinutes: 420` beside it for four months —
 * passing the "< 15 min" filter, wearing a `quick` badge and winning weeknight
 * dinner slots — because the enrich that filled in the cook time never went
 * back and recomputed the total.
 *
 * Shared by the enrich endpoint, the daily content-backfill cron and
 * `tooling/auditRecipeTimings.ts` so all three answer the question the same
 * way.
 */

import { parseRecipeDuration } from './parseRecipeDuration';

/** The three minute fields as they are stored on a recipe document. */
export interface StoredTiming {
  readyInMinutes?: number | null;
  preparationMinutes?: number | null;
  cookingMinutes?: number | null;
  /**
   * Who stated the time: `page` (the source said it), `person` (a reviewer),
   * `default` (the import's filler), `model` (derived from the step timeline).
   * Absent on recipes saved before 2026-10-12, where a bare 30 is read as the
   * filler (isDefaultTime).
   */
  timeSource?: TimeSource | null;
}

/** Every value `timeSource` may hold: the one list a whitelist reads, so a new source cannot be dropped by one. */
export const TIME_SOURCES = ['page', 'person', 'default', 'model'] as const;
export type TimeSource = (typeof TIME_SOURCES)[number];

export const isTimeSource = (v: unknown): v is TimeSource => TIME_SOURCES.includes(v as TimeSource);

/** The three ISO-8601 / prose fields as a scrape returns them. */
export interface ScrapedTiming {
  totalTime?: string | number | null;
  prepTime?: string | number | null;
  cookTime?: string | number | null;
}

const minutes = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * A total that something other than a page owns: a cook's answer stands, and a
 * model's timeline sets it from the steps (#850), where the hands-on time may
 * well exceed the page's prep. No automated pass changes it.
 */
const ownsTotal = (doc: StoredTiming): boolean => doc.timeSource === 'person' || doc.timeSource === 'model';

/**
 * A stored total smaller than the prep and cook minutes stored next to it.
 * Repairable by arithmetic alone — no scrape needed. Never for a total a
 * person or a model owns: Préparation overlaps the cook, so the parts may
 * legitimately add up to more than the total.
 */
export function hasUnderstatedTime(doc: StoredTiming): boolean {
  if (ownsTotal(doc)) return false;
  const parts = minutes(doc.preparationMinutes) + minutes(doc.cookingMinutes);
  return parts > 0 && minutes(doc.readyInMinutes) < parts;
}

/** No usable time at all. 0 and absent both mean "nobody knows". */
export function hasNoTime(doc: StoredTiming): boolean {
  return minutes(doc.readyInMinutes) === 0;
}

/**
 * The import's filler: a bare 30-minute total with no prep and no cook beside
 * it. `api/recipes/import.ts` writes 30 when a page gives no time, so it is a
 * placeholder, not a measurement: 324 catalogue recipes on 2026-10-11. It may
 * be replaced by anything a source or a person says, lower included.
 */
export function isDefaultTime(doc: StoredTiming): boolean {
  // A bare 30 can be real: NYT Cooking states "Total Time: 30 minutes" and
  // nothing else. Only a recorded source tells the two apart.
  // `default` alone proves nothing: a cook may have typed a new time since.
  // Every other recorded source stated it, so a new source needs no edit here.
  if (isTimeSource(doc.timeSource) && doc.timeSource !== 'default') return false;
  return (
    minutes(doc.readyInMinutes) === 30 &&
    minutes(doc.preparationMinutes) === 0 &&
    minutes(doc.cookingMinutes) === 0
  );
}

/** A time someone actually stated: not missing, not the import's filler. */
export function hasRealTime(doc: StoredTiming): boolean {
  return !hasNoTime(doc) && !isDefaultTime(doc);
}

/**
 * The total equals the prep time and no cook time is recorded.
 *
 * Suspicious, not broken: plenty of these are genuinely no-cook (salads,
 * ceviches, overnight oats). Worth re-scraping once, never worth treating as a
 * permanent defect — that would put every salad into a daily re-scrape loop.
 */
export function hasPrepOnlyTime(doc: StoredTiming): boolean {
  return (
    minutes(doc.cookingMinutes) === 0 &&
    minutes(doc.preparationMinutes) > 0 &&
    minutes(doc.readyInMinutes) === minutes(doc.preparationMinutes)
  );
}

export type TimingUpdate = Partial<Record<'readyInMinutes' | 'preparationMinutes' | 'cookingMinutes', number>> & {
  timeSource?: TimeSource;
};

/**
 * The fields to write, given what is stored and (optionally) a fresh scrape.
 * Returns an empty object when nothing should change.
 *
 * Two rules:
 *  - **Raise only.** A stored time larger than the source's may well be a
 *    cook's own correction, and no automated pass should argue with it. The
 *    exception is the import's 30-minute filler (isDefaultTime), which nobody
 *    stated: a source's 20 minutes replaces it.
 *  - **Never below prep + cook.** That contradiction is the defect itself.
 *
 * A total a person or a model owns (ownsTotal) is never touched, nor is its
 * `timeSource`; a missing prep or cook is still filled.
 */
export function resolveTiming(
  existing: StoredTiming,
  scraped?: ScrapedTiming,
): TimingUpdate {
  const update: TimingUpdate = {};

  const scrapedPrep = parseRecipeDuration(scraped?.prepTime);
  const scrapedCook = parseRecipeDuration(scraped?.cookTime);
  const scrapedTotal = parseRecipeDuration(scraped?.totalTime);

  if (minutes(existing.preparationMinutes) === 0 && scrapedPrep > 0) {
    update.preparationMinutes = scrapedPrep;
  }
  if (minutes(existing.cookingMinutes) === 0 && scrapedCook > 0) {
    update.cookingMinutes = scrapedCook;
  }
  // A scrape with a total but no cook time still tells us the cook time.
  if (
    minutes(existing.cookingMinutes) === 0 &&
    update.cookingMinutes === undefined &&
    scrapedCook === 0 &&
    scrapedTotal > 0
  ) {
    const prep = update.preparationMinutes ?? minutes(existing.preparationMinutes);
    if (prep > 0 && scrapedTotal > prep) update.cookingMinutes = scrapedTotal - prep;
  }

  if (ownsTotal(existing)) return update;

  const prep = update.preparationMinutes ?? minutes(existing.preparationMinutes);
  const cook = update.cookingMinutes ?? minutes(existing.cookingMinutes);
  const ready = Math.max(scrapedTotal, prep + cook);
  const stored = isDefaultTime(existing) ? 0 : minutes(existing.readyInMinutes);

  if (ready > stored && ready !== minutes(existing.readyInMinutes)) update.readyInMinutes = ready;

  // The source stated a time for a recipe that had none of its own: record
  // that, so a page's own "30 minutes" stops reading as the import's filler.
  const stated = scrapedTotal > 0 || scrapedPrep > 0 || scrapedCook > 0;
  if (stated && !hasRealTime(existing) && !existing.timeSource) update.timeSource = 'page';

  return update;
}
